import React, { useEffect, useRef, useState } from "react";
import { getModuleMetadata } from "../QueryBuilder/queryBuilderApi";
import { HIDDEN_FIELD_KEYS } from "../QueryBuilder/helpers";
import ReminderRuleFields from "./ReminderRuleFields";
import {
  RULE_STEPS,
  emptyRuleForm,
  ruleToForm,
  validateRuleForm,
  validateRuleStep,
} from "./reminderConstants";

/**
 * Form for creating or editing a reminder rule.
 *
 * Creating is a single form. Editing a saved rule is split across two steps:
 * step one is what the reminder watches (the saved query and its due-date
 * field), step two is when it fires and who receives it — so changing an
 * existing rule is a deliberate pass over both halves rather than one long form.
 *
 * Editing seeds the form from `initialRule` once, at mount. The parent remounts
 * this component with a `key` when the edited rule changes, so there is no
 * prop→state syncing to keep straight.
 * @param {object} props
 * @param {Array<object>} props.savedQueries the saved queries a rule can target.
 * @param {object} [props.initialRule] the rule being edited, or undefined to create.
 * @param {(form: object) => Promise<{ok: boolean, message?: string}>} props.onSubmit persists the rule.
 * @param {() => void} [props.onCancel] leaves edit mode.
 * @returns {JSX.Element}
 */
export default function ReminderRuleForm({
  savedQueries,
  initialRule,
  onSubmit,
  onCancel,
}) {
  const isEditing = !!initialRule;
  const [form, setForm] = useState(() =>
    initialRule ? ruleToForm(initialRule) : emptyRuleForm(),
  );
  const [stepIndex, setStepIndex] = useState(0);
  const [recipientLabel, setRecipientLabel] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  /** Once the admin picks a template, stop overriding it from dataset defaults. */
  const templateTouchedRef = useRef(isEditing);

  /** Only a saved rule is edited in steps; creating stays a single form. */
  const stepped = isEditing;
  const step = RULE_STEPS[stepIndex];
  const set = (patch) => {
    if (patch.templateKey !== undefined) templateTouchedRef.current = true;
    setForm((prev) => ({ ...prev, ...patch }));
  };

  /**
   * Loads the selected saved query's date fields. Talking to the API is an
   * external system, so it belongs in an effect; the default due-date field is
   * only applied when the current one isn't valid for this query.
   */
  useEffect(() => {
    const sq = savedQueries.find(
      (q) => String(q.id) === String(form.savedQueryId),
    );
    if (!sq) {
      setRecipientLabel(null);
      return undefined;
    }
    let active = true;
    getModuleMetadata(sq.moduleKey).then((res) => {
      if (!active) return;
      // Each dataset declares the date reminders fire on, so the admin no longer
      // picks it. Fall back to any non-hidden date field only if the dataset
      // omits its declaration.
      const dates =
        res.isSuccess && res.data
          ? res.data.fields.filter(
              (f) => f.type === "date" && !HIDDEN_FIELD_KEYS.includes(f.key),
            )
          : [];
      const anchorField =
        (res.isSuccess ? res.data?.dueDateField : null) || dates[0]?.key || "";
      setRecipientLabel(res.isSuccess ? res.data?.recipientLabel ?? null : null);
      const suggestedTemplate = res.isSuccess
        ? res.data?.defaultTemplateKey
        : null;
      setForm((prev) => {
        const next = { ...prev };
        if (prev.dueDateField !== anchorField) {
          next.dueDateField = anchorField;
        }
        if (suggestedTemplate && !templateTouchedRef.current) {
          next.templateKey = suggestedTemplate;
        }
        return next;
      });
    });
    return () => {
      active = false;
    };
  }, [form.savedQueryId, savedQueries]);

  const goNext = () => {
    const problem = validateRuleStep(step.key, form);
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setStepIndex((i) => i + 1);
  };

  const goBack = () => {
    setError(null);
    setStepIndex((i) => i - 1);
  };

  const submit = async () => {
    const problem = stepped
      ? validateRuleStep(step.key, form)
      : validateRuleForm(form);
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setBusy(true);
    const res = await onSubmit(form);
    setBusy(false);
    if (!res.ok) {
      setError(res.message || "Failed to save rule");
      return;
    }
    if (isEditing) return;
    setForm(emptyRuleForm());
    setStepIndex(0);
  };

  const isLastStep = stepIndex === RULE_STEPS.length - 1;

  return (
    <div className="rem-form">
      <h3 className="rem-subtitle">
        {isEditing ? `Edit rule — ${initialRule.name}` : "New reminder rule"}
      </h3>

      {stepped && (
        <ol className="rem-steps">
          {RULE_STEPS.map((s, i) => (
            <li
              key={s.key}
              className={`rem-step ${i === stepIndex ? "rem-step-active" : ""}`}
            >
              {s.label}
            </li>
          ))}
        </ol>
      )}

      {stepped ? (
        <ReminderRuleFields
          form={form}
          onChange={set}
          savedQueries={savedQueries}
          recipientLabel={recipientLabel}
          step={step.key}
        />
      ) : (
        RULE_STEPS.map((s) => (
          <ReminderRuleFields
            key={s.key}
            form={form}
            onChange={set}
            savedQueries={savedQueries}
            recipientLabel={recipientLabel}
            step={s.key}
          />
        ))
      )}

      {error && (
        <div className="rem-error" role="alert">
          {error}
        </div>
      )}

      <div className="rem-actions">
        {stepped && stepIndex > 0 && (
          <button
            type="button"
            className="rem-btn rem-btn-ghost"
            onClick={goBack}
          >
            Back
          </button>
        )}
        {stepped && !isLastStep && (
          <button
            type="button"
            className="rem-btn rem-btn-primary"
            onClick={goNext}
          >
            Next
          </button>
        )}
        {(!stepped || isLastStep) && (
          <button
            type="button"
            className="rem-btn rem-btn-primary"
            onClick={submit}
            disabled={busy}
          >
            {busy
              ? "Saving…"
              : isEditing
                ? "Save changes"
                : "Create reminder"}
          </button>
        )}
        {isEditing && onCancel && (
          <button
            type="button"
            className="rem-btn rem-btn-ghost"
            onClick={onCancel}
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
